import { Eye, Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { useToggleWatch, useWatchTargets, type WatchType } from '@/hooks/useWatch';

export const FEATURE_META = {
  id: 'watch-listing',
  label: 'Watch / Follow',
  description:
    'Watch a listing or follow a supplier so price drops, sold-out changes, and new inventory reach you without checking back.',
  triggerType: 'action' as const,
  personas: ['buyer', 'buyer_seller', 'seller'] as ('buyer' | 'buyer_seller' | 'seller')[],
};

interface WatchButtonProps {
  watchType: WatchType;
  listingId?: string | null;
  supplierCompanyId?: string | null;
  savedSearchId?: string | null;
  menuId?: string | null;
  variant?: 'icon' | 'full';
  size?: 'sm' | 'default';
  className?: string;
  /** Label override for the full variant. */
  label?: string;
}

/**
 * Single control for every kind of watch. Reads the page-level watch-target
 * list (one query) so dozens of cards cost nothing extra.
 */
const WatchButton = ({
  watchType,
  listingId,
  supplierCompanyId,
  savedSearchId,
  menuId,
  variant = 'icon',
  size = 'sm',
  className,
  label,
}: WatchButtonProps) => {
  useFeatureRegistration(FEATURE_META);
  const { data: targets = [], isError: targetsError, isLoading: targetsLoading, refetch } = useWatchTargets();
  const toggle = useToggleWatch();

  const isWatched = targets.some(
    (t) =>
      t.watch_type === watchType &&
      (watchType !== 'listing' || t.listing_id === listingId) &&
      (watchType !== 'supplier' || t.supplier_company_id === supplierCompanyId) &&
      (watchType !== 'menu' || t.menu_id === menuId) &&
      (watchType !== 'search' || t.saved_search_id === savedSearchId),
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    /* Unknown state: toggling would guess wrong and could silently unfollow. */
    if (targetsError) {
      void refetch();
      return;
    }
    toggle.mutate({ watchType, listingId, supplierCompanyId, savedSearchId, menuId, isWatched });
  };

  const isBellStyle = watchType === 'supplier' || watchType === 'menu';
  const OnIcon = isBellStyle ? Bell : Eye;
  const OffIcon = isBellStyle ? BellOff : Eye;

  let text: string;
  let tip: string;
  if (targetsError) {
    text = label ?? 'Retry';
    tip = "We couldn't check whether you're following this — tap to retry";
  } else if (watchType === 'supplier') {
    text = label ?? (isWatched ? 'Following supplier' : 'Follow supplier');
    tip = isWatched
      ? 'Stop following this supplier'
      : 'Follow the supplier: hear about everything they share with you, across all their menus';
  } else if (watchType === 'menu') {
    text = label ?? (isWatched ? 'Alerts on' : 'Alert me on this menu');
    tip = isWatched
      ? 'Turn off alerts for this menu'
      : 'Alert me on this menu only — quieter than following the whole supplier';
  } else {
    text = label ?? (isWatched ? 'Watching' : 'Watch');
    tip = isWatched
      ? 'Stop watching this listing'
      : 'Watch: get told about price drops and sold-out changes';
  }

  const Icon = toggle.isPending || targetsLoading ? Loader2 : isWatched ? OnIcon : OffIcon;
  const spinning = toggle.isPending || targetsLoading;

  const button = (
    <Button
      type="button"
      variant="outline"
      size={variant === 'icon' ? 'icon' : size}
      onClick={handleClick}
      disabled={toggle.isPending || targetsLoading}
      aria-label={tip}
      aria-pressed={targetsError ? undefined : isWatched}
      className={cn(
        variant === 'icon' && (size === 'sm' ? 'h-7 w-7' : 'h-9 w-9'),
        isWatched && !targetsError && 'text-primary bg-primary/10 border-primary/40',
        targetsError && 'text-muted-foreground',
        className,
      )}
    >
      <Icon className={cn('w-3.5 h-3.5', spinning && 'animate-spin')} />
      {variant === 'full' && <span className="ml-1.5">{text}</span>}
    </Button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
};

export default WatchButton;